import { Eye, FileText, PlusCircle, Trash2, Pencil } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

import {
  useCreateCargoOrder1,
  useGetAggregateData,
  OrderPageResponseDTOStatus,
  useDeleteCargoOrder,
} from "@/common/api";
import {
  Badge,
  Button,
  FilterBar,
  StatusCards,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TruncateTooltip,
  Card,
  CardContent,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { useUser, useAuth, useToast } from "@/common/hooks";
import { getFilterOnStatus, getAppText, getStatusData, formatDate } from "@/common/lib";

const LIMIT = 10;

export function DashboardPage() {
  const {
    auth: { id, role, isAdmin },
  } = useAuth();
  const { permissions } = useUser();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { mutateAsync: mutateDeleteAsync } = useDeleteCargoOrder();

  const statusesData = useGetAggregateData(
    {
      id: id!,
    },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );
  const statuses = statusesData.data?.data.payload;

  const [page, setPage] = useState(1);

  const [createdAt, setCreatedAt] = useState("");
  const [request, setRequest] = useState("");
  const [status, setStatus] = useState<string>();

  const { data, isLoading, refetch } = useCreateCargoOrder1(
    {
      userId: id!,
      orderDate: createdAt,
      orderNumber: request,
      statusId: status,
      page: page,
      limit: LIMIT,
    },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const totalPages = data?.data.payload?.totalPage;
  const appText = useMemo(() => getAppText(role), [role]);
  const totalItems = data?.data.payload?.totalItems;
  const items = data?.data.payload?.items ?? [];

  const handleReset = useCallback(() => {
    setStatus("");
    setCreatedAt("");
    setRequest("");
  }, []);

  const handleDeleteOrder = useCallback((orderId: string) => {
    mutateDeleteAsync({
      params: { id: orderId! },
    })
      .then(() => refetch())
      .then(() => {
        toast({
          title: "Заявка удалена",
          description: "Заявка успешно удалена",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка удвления заявки",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при удалении заявки",
        });
      });
  }, []);

  const handleRowClick = useCallback(
    (itemId: string, isEditable?: boolean) => {
      if (isEditable) {
        setLocation(ROUTES.EDIT_ORDER.replace(":id", itemId));
      } else {
        // При переходе с DashboardPage participant в URL всегда 1 (истина)
        setLocation(`${ROUTES.ORDER_ID.replace(":id", itemId)}?participant=1`);
      }
    },
    [setLocation],
  );

  const handleEditClick = useCallback(
    (itemId: string) => {
      setLocation(ROUTES.EDIT_ORDER.replace(":id", itemId));
    },
    [setLocation],
  );

  const statsCards = useMemo(() => {
    if (statuses?.length) {
      const newStatus = statuses.map((status) => getFilterOnStatus(status));
      const lastElement = newStatus.splice(newStatus.length - 1, 1)[0];
      newStatus.splice(0, 0, lastElement);
      return newStatus;
    }
  }, [statuses]);

  const rows = useMemo(
    () => (
      <>
        {items.map((item) => {
          const isEditable = item.status === OrderPageResponseDTOStatus.ON_REVISION && item.isOwner;

          return (
            <TableRow
              key={item.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => handleRowClick(item.id!, isEditable)}
            >
              <TableCell className="text-center font-medium">{item.orderNumber}</TableCell>
              <TableCell className="text-center ">
                <TruncateTooltip
                  text={item.loadingStation ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm font-medium">{item.loadingRoad && `(${item.loadingRoad})`}</p>
              </TableCell>
              <TableCell className="text-center flex flex-col items-center">
                <TruncateTooltip
                  text={item.cargoName ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm">({item.cargoWeight} т.)</p>
              </TableCell>

              <TableCell className="text-center">
                <TruncateTooltip
                  text={item.wagonsType ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm">{item.totalWagons && `(${item.totalWagons} в.)`}</p>
              </TableCell>
              <TableCell className="text-center">{item.participantsCount}</TableCell>
              <TableCell className="text-center">
                {/* STATUS_LABELS */}
                <Badge className={getStatusData(item.status).color}>
                  {getStatusData(item.status).label}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                {item.orderCreation && formatDate(item.orderCreation)}
              </TableCell>
              <TableCell className="flex justify-center gap-2">
                {isEditable ? (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(item.id!);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteOrder(item.id!);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(item.id!, isEditable);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </>
    ),
    [items, setLocation, handleDeleteOrder, handleRowClick, handleEditClick],
  );

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center gap-4 md:flex-nowrap flex-wrap">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>
            {appText.title}
          </h2>
          <p className="text-lg font-medium text-gray-700 mt-2">{appText.subtitle}</p>
        </div>

        <div className="flex flex-wrap gap-2 justify-start md:justify-end">
          {permissions?.applications.create_application && (
            <Link href={ROUTES.CREATE_ORDER}>
              <Button
                variant="default"
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
              >
                <PlusCircle className="h-4 w-4" />
                Создать заявку на перевозку груза
              </Button>
            </Link>
          )}
          <Link href="/drafts">
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Сохраненные черновики
            </Button>
          </Link>
        </div>
      </div>

      {/* Statuses */}
      {statsCards && (
        <StatusCards
          cards={statsCards}
          activeStatusId={status}
          onStatusClick={(statusId) => {
            setPage(1);
            setStatus(statusId);
          }}
          className="mb-6"
        />
      )}

      {/* Filters */}
      <FilterBar
        className="mb-6"
        createdAt={createdAt}
        onCreatedAtChange={setCreatedAt}
        orderNumber={request}
        onOrderNumberChange={setRequest}
        onReset={handleReset}
      />

      {/* Table */}
      <div className="mt-6">
        <Card>
          <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
            {isLoading ? (
              <div className="text-center py-8">
                <p className="caption">Загрузка...</p>
              </div>
            ) : data?.data.payload?.items?.length === 0 ? (
              <div className="text-center py-8">
                <p className="caption">Нет инициированных процедур</p>
              </div>
            ) : (
              <Table className="">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-start text-xs font-semibold leading-4 text-Text-secondary">
                      № заявки
                    </TableHead>
                    <TableHead className="text-center w-44">Станция отправления (дорога)</TableHead>
                    <TableHead className="text-center">Груз</TableHead>
                    <TableHead className="text-center">Вагоны</TableHead>
                    <TableHead className="text-center">Количество участников</TableHead>
                    <TableHead className="text-center">Статус</TableHead>
                    <TableHead className="text-center">Дата создания</TableHead>
                    <TableHead className="text-center">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{rows}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <TablePagination
          currentPage={page}
          totalPages={totalPages ?? 0}
          totalItems={totalItems ?? 0}
          pageSize={LIMIT}
          onPageChange={(page) => {
            setPage(page);
          }}
        />
      </div>
    </>
  );
}
